import { useEffect, useRef, useContext, useCallback } from "react";
import { get, isEqual } from "lodash";
import { WidgetContext } from "../store/widgetContext";
import { axiosChat, axiosTickets, axiosAgentService } from "../configs/http-config";
import { useSession } from "../session/useSession";
import { SessionManager } from "../session/SessionManager";
import eventEmitter from "../utils/eventEmitter";
import { setWwsIds } from "../utils/widgetStorage";
import notificationSound from "../assets/sounds/notification.wav";

/**
 * Hook that runs all widget data side effects: fetch conversations, contact, tickets,
 * assistant; subscribes to eventEmitter (agents_update, record_update, etc.).
 * Must be used inside WidgetContextProvider.
 */
export function useWidgetData(props) {
	const { botId, tenantId, uuid, projectUUID, assistantId } = props;
	const { sessionId } = useSession();
	const context = useContext(WidgetContext);
	const {
		widget,
		contact,
		assistant,
		setConversations,
		setConversationsPagination,
		setCallConversations,
		setCallConversationsPagination,
		fetchMoreConversationsRef,
		setTickets,
		setContact,
		setAgents,
		setAssistant,
		isMuted,
	} = context;

	const agentsRef = useRef([]);
	const isMutedRef = useRef(false);

	useEffect(() => {
		isMutedRef.current = isMuted;
	}, [isMuted]);

	const sanitizeRecords = useCallback((records) => {
		return (records || []).filter((record) => {
			if (record.reply?.type === "contact_data_collector") return true;
			if (record.reply?.type === "data_collector" && Array.isArray(record.reply?.config?.attrs)) {
				const hasEmptyAttr = record.reply.config.attrs.some((attr) => !attr.hasOwnProperty("value"));
				if (!hasEmptyAttr) return false;
			}
			return true;
		});
	}, []);

	const sanitizeConversations = useCallback(
		(conversations) =>
			(conversations || []).map(({ placeholder, ...conv }) => ({
				...conv,
				records: sanitizeRecords(conv.records),
			})),
		[sanitizeRecords]
	);

	const setEmptyConversations = useCallback(() => {
		setConversations((prev) => {
			const hasReal = Array.isArray(prev) && prev.some((c) => c._id && !c.placeholder);
			if (hasReal) return prev;
			return [{ _id: null, placeholder: true, records: [] }];
		});
	}, [setConversations]);

	const fetchConversations = useCallback(
		async (fullContactId, botId, tenantId, projectUUID, type, sessionId) => {
			try {
				const sessionParam = sessionId ? `&sessionId=${sessionId}` : "";
				const response = await axiosChat.get(
					`/conversations/all/${fullContactId}?botId=${botId}&uuid=${projectUUID}&tenantId=${tenantId}${type ? `&type=${type}` : ""}${sessionParam}`
				);
				if (response.status === 200) {
					const data = get(response, "data.data", {});
					const fetchedData = data.conversations || (Array.isArray(data) ? data : []);
					const pagination = data.pagination || null;

					if (fetchedData.length > 0) {
						const sanitized = sanitizeConversations(fetchedData);
						if (!type || type === "chats") {
							setConversations(prev => {
								const serverIds = new Set(sanitized.map(c => c._id));
								const localOnly = prev.filter(c => c._id && !c.placeholder && !serverIds.has(c._id));
								return [...sanitized, ...localOnly];
							});
							setConversationsPagination(pagination);
						} else if (type === "calls") {
							setCallConversations(prev => {
								const serverIds = new Set(sanitized.map(c => c._id));
								const localOnly = prev.filter(c => c._id && !c.placeholder && !serverIds.has(c._id));
								return [...sanitized, ...localOnly];
							});
							setCallConversationsPagination(pagination);
						}
					} else if (!type || type === "chats") {
						setEmptyConversations();
						setConversationsPagination(pagination);
					}
				}
			} catch (error) {
				console.error("@@ Error: fetchConversations @@", error);
				if (!type || type === "chats") setEmptyConversations();
			}
		},
		[sanitizeConversations, setConversations, setConversationsPagination, setCallConversations, setCallConversationsPagination, setEmptyConversations]
	);

	const fetchMoreConversations = useCallback(
		async (fullContactId, botId, tenantId, projectUUID, type, sessionId, page) => {
			try {
				const sessionParam = sessionId ? `&sessionId=${sessionId}` : "";
				const response = await axiosChat.get(
					`/conversations/all/${fullContactId}?botId=${botId}&uuid=${projectUUID}&tenantId=${tenantId}${type ? `&type=${type}` : ""}${sessionParam}&page=${page}&limit=20`
				);
				if (response.status === 200) {
					const data = get(response, "data.data", {});
					const fetchedData = data.conversations || (Array.isArray(data) ? data : []);
					const pagination = data.pagination || null;

					if (fetchedData.length > 0) {
						const sanitized = sanitizeConversations(fetchedData);
						if (!type || type === "chats") {
							setConversations(prev => {
								const existingIds = new Set(prev.map(c => c._id));
								const unique = sanitized.filter(c => !existingIds.has(c._id));
								return [...prev, ...unique];
							});
							setConversationsPagination(pagination);
						} else if (type === "calls") {
							setCallConversations(prev => {
								const existingIds = new Set(prev.map(c => c._id));
								const unique = sanitized.filter(c => !existingIds.has(c._id));
								return [...prev, ...unique];
							});
							setCallConversationsPagination(pagination);
						}
					} else {
						if (!type || type === "chats") setConversationsPagination(pagination);
						else if (type === "calls") setCallConversationsPagination(pagination);
					}
				}
			} catch (error) {
				console.error("@@ Error: fetchMoreConversations @@", error);
			}
		},
		[sanitizeConversations, setConversations, setConversationsPagination, setCallConversations, setCallConversationsPagination]
	);

	fetchMoreConversationsRef.current = fetchMoreConversations;

	const fetchContact = useCallback(
		async (fullContactId, botId, tenantId, projectUUID, sessionId) => {
			try {
				const sessionParam = sessionId ? `&sessionId=${sessionId}` : "";
				const response = await axiosChat.get(
					`/contacts/wws/${fullContactId}?botId=${botId}&uuid=${projectUUID}&tenantId=${tenantId}${sessionParam}`
				);
				if (response.status === 200) {
					const data = get(response, "data.data", null);
					if (data) setContact(data);
				}
			} catch (error) {
				console.error("@@ Error: fetchContact @@", error);
			}
		},
		[setContact]
	);

	const fetchTickets = useCallback(
		async (fullContactId, botId, tenantId, sessionId) => {
			try {
				const sessionParam = sessionId ? `&sessionId=${sessionId}` : "";
				const response = await axiosTickets.get(
					`/tickets/chat-widget/contact/${fullContactId}?botId=${botId}&tenantId=${tenantId}${sessionParam}`
				);
				if (response.status === 200) {
					const data = get(response, "data.data", []);
					setTickets(data);
				}
			} catch (error) {
				console.error("@@ Error: fetchTickets @@", error);
			}
		},
		[setTickets]
	);

	const handleAgents = useCallback(
		(message) => {
			try {
				const { agents: agentsList } = message;
				if (Array.isArray(agentsList)) {
					const allAgents = agentsList.filter((el) => el.name && el.profileURL);
					if (!isEqual(agentsRef.current, allAgents)) {
						setAgents(allAgents);
						agentsRef.current = allAgents;
					}
				}
			} catch (error) {
				console.error("@@ Error: handleAgents @@", error);
			}
		},
		[setAgents]
	);

	const mergeConversationRecords = useCallback((records, newRecord) => {
		let recordExists = false;
		const updated = (records || []).map((record) => {
			if (record._id === newRecord._id) {
				recordExists = true;
				return newRecord;
			}
			return record;
		});
		if (!recordExists) updated.push({ ...newRecord });
		return updated;
	}, []);

	const updateConversationRecords = useCallback(
		(record) => {
			setConversations((prev) => {
				let matched = false;
				const updated = prev.map((conv) => {
					const result = { ...conv };
					if (result.placeholder && !result._id) {
						result._id = record.conversationId;
						result.placeholder = false;
					}
					if (result._id === record.conversationId) {
						result.records = mergeConversationRecords(result.records, record);
						matched = true;
					}
					return result;
				});
				if (!matched) {
					updated.push({
						_id: record.conversationId,
						records: [record],
						tenantId: record.tenantId,
						projectUUID: record.projectUUID,
						contactId: record.contactId,
					});
				}
				return updated;
			});
		},
		[setConversations, mergeConversationRecords]
	);

	const playNotificationSound = useCallback(() => {
		if (isMutedRef.current) return;
		const audio = new Audio(notificationSound);
		audio.play().catch((err) => console.error("Error playing sound:", err));
	}, []);

	const handleUpdateRecord = useCallback(
		(message) => {
			try {
				const { contactId, record } = message;
				const fullContactId = SessionManager.getSocketContactId();
				if (contactId == fullContactId) {
					playNotificationSound();
					updateConversationRecords(record);
				}
			} catch (error) {
				console.error("@@ Error: handleUpdateRecord @@", error);
			}
		},
		[playNotificationSound, updateConversationRecords]
	);

	const handleTicketCreated = useCallback(
		(message) => {
			try {
				const fullContactId = SessionManager.getSocketContactId();
				if (message.contactId == fullContactId) {
					fetchTickets(SessionManager.getSocketContactId(), botId, tenantId, sessionId);
				}
			} catch (error) {
				console.error("@@ Error: handleTicketCreated @@", error);
			}
		},
		[botId, tenantId, sessionId, fetchTickets]
	);

	const handleContactUpdate = useCallback(
		(message) => {
			if (message.contact) setContact(message.contact);
		},
		[setContact]
	);

	const handleConversationStatusUpdate = useCallback(
		(message) => {
			const { conversationId, status, contactId } = message;
			const fullContactId = SessionManager.getSocketContactId();
			if (contactId !== fullContactId) return;
			setConversations((prev) =>
				prev.map((conv) =>
					conv._id === conversationId ? { ...conv, status } : conv
				)
			);
		},
		[setConversations]
	);

	const handleConvAssignee = useCallback(
		(message) => {
			const { prevConvId, newConvId } = message;
			if (prevConvId && newConvId) {
				setTickets((prev) =>
					prev?.map((ticket) =>
						ticket?.conversationId === prevConvId ? { ...ticket, conversationId: newConvId } : ticket
					)
				);
			}
			const effectiveContactId = SessionManager.getSocketContactId();
			fetchConversations(effectiveContactId, botId, tenantId, projectUUID, "chats", sessionId);
			fetchConversations(effectiveContactId, botId, tenantId, projectUUID, "calls", sessionId);
		},
		[botId, tenantId, projectUUID, sessionId, setTickets, fetchConversations]
	);

	// Assistant is normally set by fetchWidget (bootstrap in useBootstrap). This is a fallback only when assistant is still missing (e.g. race or direct load).
	// For swarm-connected bots, the embed-script `assistantId` is the swarm `_id`;
	// prefer `widget.primaryAssistantId` (resolved by UCS) when present.
	useEffect(() => {
		if (!assistant && assistantId && tenantId && uuid) {
			const effectiveAssistantId = widget?.primaryAssistantId || assistantId;
			setWwsIds({ assistantId, botId, tenantId, uuid: projectUUID });
			axiosAgentService
				.get(`/assistants/messenger/${effectiveAssistantId}`, { params: { tenantId, uuid } })
				.then((response) => {
					if (response?.data?.data) setAssistant(response.data.data);
				})
				.catch((err) => console.error("Error fetching assistant:", err));
		}
	}, [assistant, assistantId, tenantId, uuid, botId, projectUUID, setAssistant, widget?.primaryAssistantId]);

	useEffect(() => {
		const listeners = [
			["agents_update", handleAgents],
			["record_update", handleUpdateRecord],
			["ticket_created", handleTicketCreated],
			["conversation_assignee", handleConvAssignee],
			["contact_update", handleContactUpdate],
			["conversation_status_update", handleConversationStatusUpdate],
		];
		listeners.forEach(([event, handler]) => eventEmitter.on(event, handler));
		return () => listeners.forEach(([event, handler]) => eventEmitter.off(event, handler));
	}, [handleAgents, handleUpdateRecord, handleTicketCreated, handleConvAssignee, handleContactUpdate, handleConversationStatusUpdate]);

	useEffect(() => {
		if (uuid && widget && tenantId && botId) {
			const effectiveContactId = SessionManager.getSocketContactId();
			fetchContact(effectiveContactId, botId, tenantId, projectUUID, sessionId);
			fetchConversations(effectiveContactId, botId, tenantId, projectUUID, "chats", sessionId);
			fetchConversations(effectiveContactId, botId, tenantId, projectUUID, "calls", sessionId);
		}
	}, [uuid, widget, tenantId, botId, projectUUID, sessionId, fetchContact, fetchConversations]);

	useEffect(() => {
		if (uuid && botId && tenantId) {
			const effectiveContactId = SessionManager.getSocketContactId();
			fetchTickets(effectiveContactId, botId, tenantId, sessionId);
		}
	}, [contact?.contactId, uuid, botId, tenantId, sessionId, fetchTickets]);
}
